(function () {
  if (window.__jklBmsmRegiosMatchInit) return;
  window.__jklBmsmRegiosMatchInit = true;

  /**
   * BMSM tiles based on Regios DOPP volume discounts.
   *
   * Goals:
   * - async-safe: does not cache an empty Regios object forever.
   * - order-safe: does not assume PRODUCT_ELIGIBILITY is conditions[0].
   * - product eligibility-safe: products, variants, collections, vendors, tags,
   *   sale status and basic metafield data when provided in data attributes.
   * - future-safe: when Regios DOPP API is available, it is used as final
   *   validation instead of attempting to clone every Regios checkout rule.
   */

  const DEBUG = !!window.__JklBmsmDebug;
  const STRICT_UNKNOWN_CONDITIONS = !!window.__JklBmsmStrictConditions;
  const MAX_CONFIG_RETRIES = Number(window.__JklBmsmMaxConfigRetries || 40);
  const CONFIG_RETRY_DELAY_MS = Number(
    window.__JklBmsmConfigRetryDelayMs || 250,
  );
  const DEFAULT_CURRENCY_SYMBOL = window.__JklBmsmCurrencySymbol || "€";

  const log = (...a) => DEBUG && console.log("[BMSM]", ...a);
  const warn = (...a) => DEBUG && console.warn("[BMSM]", ...a);

  let raf = null;
  let retryTimer = null;
  let retryCount = 0;
  let renderSeq = 0;
  let lastGoodConfigSignature = "";
  let lastGoodConfigs = [];

  function safeJsonParse(value) {
    if (value == null || value === "" || value === "null") return null;
    if (typeof value === "object") return value;

    try {
      return JSON.parse(String(value));
    } catch (e) {
      return null;
    }
  }

  function normalizeStr(value) {
    return String(value == null ? "" : value).trim();
  }

  function normalizeKey(value) {
    return normalizeStr(value).toLowerCase();
  }

  function unique(values) {
    return Array.from(new Set(values.filter((v) => v !== "" && v != null)));
  }

  function parseList(value) {
    if (!value) return [];

    if (Array.isArray(value)) {
      return unique(value.flatMap((x) => parseList(x)));
    }

    if (typeof value === "object") {
      return unique(Object.values(value).flatMap((x) => parseList(x)));
    }

    const str = String(value).trim();
    if (!str) return [];

    const parsed = safeJsonParse(str);
    if (parsed && typeof parsed === "object" && parsed !== str)
      return parseList(parsed);

    return unique(
      str
        .split(/[,\s]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    );
  }

  function parseIdList(value) {
    return parseList(value).map(String).filter(Boolean);
  }

  function parseJsonAttribute(value, fallback) {
    const parsed = safeJsonParse(value);
    return parsed == null ? fallback : parsed;
  }

  function toNumber(value) {
    if (value == null || value === "") return null;
    const n = Number(String(value).replace(/[^0-9.-]/g, ""));
    return Number.isFinite(n) ? n : null;
  }

  function toInt(value) {
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? n : null;
  }

  function toBool(value) {
    if (typeof value === "boolean") return value;
    if (value == null || value === "") return null;
    const s = normalizeKey(value);
    if (["true", "1", "yes", "y", "on"].includes(s)) return true;
    if (["false", "0", "no", "n", "off"].includes(s)) return false;
    return null;
  }

  function formatNumber(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    return String(Number(n.toFixed(4))).replace(/\.0+$/, "");
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === "function") {
      return window.CSS.escape(String(value));
    }
    return String(value).replace(/[^a-zA-Z0-9_-]/g, "\\$&");
  }

  function objectSignature(obj) {
    try {
      return JSON.stringify(obj);
    } catch (e) {
      return String(Date.now());
    }
  }

  function readRegiosConfigs() {
    const dc = window.RegiosDOPP && window.RegiosDOPP.discountConfigs;
    if (!dc || typeof dc !== "object") return [];

    const signature = objectSignature(dc);
    if (signature === lastGoodConfigSignature && lastGoodConfigs.length) {
      return lastGoodConfigs;
    }

    const configs = Object.entries(dc)
      .map(([id, raw]) => {
        const parsed = safeJsonParse(raw);
        const config = parsed && parsed.config ? parsed.config : parsed;
        if (!config || typeof config !== "object") return null;
        return { id: String(id), raw: parsed, config };
      })
      .filter(Boolean)
      .filter((entry) => entry.config.isEnabled !== false);

    if (configs.length) {
      lastGoodConfigSignature = signature;
      lastGoodConfigs = configs;
    }

    return configs.length ? configs : [];
  }

  function readSelectedVariantId(container) {
    const formId = container.getAttribute("data-form-id");
    const form = formId
      ? document.getElementById(formId)
      : container.closest("form");
    const input =
      (form && form.querySelector('[name="id"]')) ||
      (formId &&
        document.querySelector(`[name="id"][form="${cssEscape(formId)}"]`)) ||
      document.querySelector('form[action*="/cart/add"] [name="id"]');

    if (input && input.value) return String(input.value);

    const explicit = container.getAttribute("data-variant-id");
    if (explicit) return explicit;

    const selectedVariant =
      window.RegiosDOPP_ProductPage &&
      window.RegiosDOPP_ProductPage.selectedVariant;
    if (selectedVariant && selectedVariant.id)
      return String(selectedVariant.id);

    return "";
  }

  function getQtyInput(container) {
    const formId = container.getAttribute("data-form-id");
    if (formId) {
      const byFormAttr = document.querySelector(
        `input[name="quantity"][form="${cssEscape(formId)}"]`,
      );
      if (byFormAttr) return byFormAttr;

      const form = document.getElementById(formId);
      const inForm = form && form.querySelector('input[name="quantity"]');
      if (inForm) return inForm;
    }

    const closestForm = container.closest("form");
    if (closestForm) {
      const input = closestForm.querySelector('input[name="quantity"]');
      if (input) return input;
    }

    return document.querySelector(
      'form[action*="/cart/add"] input[name="quantity"]',
    );
  }

  function getCurrentQty(container) {
    const input = getQtyInput(container);
    const qty = input ? toInt(input.value) : null;
    return qty && qty > 0 ? qty : 1;
  }

  function getCtx(container) {
    const priceInCents =
      toNumber(container.getAttribute("data-price-in-cents")) ||
      toNumber(container.getAttribute("data-regular-price-in-cents"));
    const compareAtPriceInCents = toNumber(
      container.getAttribute("data-compare-at-price-in-cents"),
    );

    const isOnSaleAttr = toBool(container.getAttribute("data-is-on-sale"));
    const isOnSale =
      isOnSaleAttr != null
        ? isOnSaleAttr
        : compareAtPriceInCents != null && priceInCents != null
          ? compareAtPriceInCents > priceInCents
          : null;

    const customer = parseJsonAttribute(
      container.getAttribute("data-customer"),
      null,
    );
    const customerTags = parseList(
      container.getAttribute("data-customer-tags"),
    );
    const customerSegments = parseIdList(
      container.getAttribute("data-customer-segment-ids"),
    )
      .map((x) => toNumber(x))
      .filter((x) => x != null);

    return {
      container,
      productId: normalizeStr(container.getAttribute("data-product-id")),
      variantId: normalizeStr(readSelectedVariantId(container)),
      collectionIds: parseIdList(container.getAttribute("data-collection-ids")),
      tags: parseList(container.getAttribute("data-product-tags")),
      vendor: normalizeStr(container.getAttribute("data-product-vendor")),
      productType: normalizeStr(container.getAttribute("data-product-type")),
      handle: normalizeStr(container.getAttribute("data-product-handle")),
      url: normalizeStr(container.getAttribute("data-product-url")),
      formId: normalizeStr(container.getAttribute("data-form-id")),
      marketId: normalizeStr(container.getAttribute("data-market-id")),
      countryCode: normalizeStr(
        container.getAttribute("data-country-code") || window.Shopify?.country,
      ),
      sellingPlanId: normalizeStr(
        container.getAttribute("data-selling-plan-id"),
      ),
      hasSellingPlan: toBool(container.getAttribute("data-has-selling-plan")),
      priceInCents,
      compareAtPriceInCents,
      isOnSale,
      quantity: getCurrentQty(container),
      productMetafields: parseJsonAttribute(
        container.getAttribute("data-product-metafields"),
        [],
      ),
      variantMetafields: parseJsonAttribute(
        container.getAttribute("data-variant-metafields"),
        [],
      ),
      lineItemProperties: parseJsonAttribute(
        container.getAttribute("data-line-item-properties"),
        undefined,
      ),
      customer:
        customer ||
        (customerTags.length || customerSegments.length
          ? { tags: customerTags, segments: customerSegments }
          : undefined),
      currencySymbol:
        container.getAttribute("data-currency-symbol") ||
        DEFAULT_CURRENCY_SYMBOL,
    };
  }

  function extractListFromKeys(obj, keys) {
    if (!obj || typeof obj !== "object") return [];

    const out = [];
    keys.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        out.push(...parseList(obj[key]));
      }
    });
    return unique(out.map(String));
  }

  function compactKeys(obj) {
    return obj && typeof obj === "object" && !Array.isArray(obj)
      ? Object.keys(obj).map(String)
      : [];
  }

  function compactValues(obj) {
    return obj && typeof obj === "object" && !Array.isArray(obj)
      ? Object.values(obj)
          .flatMap((x) => parseList(x))
          .map(String)
      : [];
  }

  function buildEligibilityCondition(type, eligibility, includeOrExclude) {
    return {
      type,
      includeOrExclude: includeOrExclude || "INCLUDE",
      eligibility: eligibility || {},
    };
  }

  function getRootPredicates(entry) {
    const cfg = entry.config || {};
    const predicates = [];

    if (
      cfg.productEligibility &&
      cfg.productEligibility.type &&
      cfg.productEligibility.type !== "ALL_PRODUCTS"
    ) {
      predicates.push({
        matchType: "AND",
        conditions: [
          buildEligibilityCondition(
            "PRODUCT_ELIGIBILITY",
            cfg.productEligibility,
            "INCLUDE",
          ),
        ],
        source: "config.productEligibility",
      });
    }

    if (
      cfg.customerEligibility &&
      cfg.customerEligibility.type &&
      cfg.customerEligibility.type !== "ALL_CUSTOMERS"
    ) {
      predicates.push({
        matchType: "AND",
        conditions: [
          buildEligibilityCondition(
            "CUSTOMER_ELIGIBILITY",
            cfg.customerEligibility,
            "INCLUDE",
          ),
        ],
        source: "config.customerEligibility",
      });
    }

    if (
      cfg.appliesToSellingPlans &&
      cfg.appliesToSellingPlans !== "ALL_SELLING_PLANS"
    ) {
      predicates.push({
        matchType: "AND",
        conditions: [
          {
            type: "SELLING_PLAN",
            includeOrExclude: "INCLUDE",
            appliesTo: cfg.appliesToSellingPlans,
          },
        ],
        source: "config.appliesToSellingPlans",
      });
    }

    return predicates;
  }

  function collectVolumeCandidates(entry) {
    const cfg = entry.config;
    const candidates = [];
    const rootPredicates = getRootPredicates(entry);
    const seen = new WeakSet();

    function addCandidate(node, predicates, path) {
      const tiers = volumeNodeToTiers(node, DEFAULT_CURRENCY_SYMBOL);
      if (!tiers.length) return;

      candidates.push({
        configId: entry.id,
        message: cfg.message || "",
        node,
        tiers,
        predicates,
        path,
      });
    }

    function walk(node, predicates, path) {
      if (!node || typeof node !== "object") return;
      if (seen.has(node)) return;
      seen.add(node);

      const type = normalizeKey(node.type).toUpperCase();
      const hasConditions =
        Array.isArray(node.conditions) && node.conditions.length > 0;
      const hasBranches = node.then || node.otherwise;

      if ((type === "CHECK_IF" || hasBranches) && hasConditions) {
        const group = {
          matchType: node.matchType || "AND",
          conditions: node.conditions || [],
          source: node.id || path,
        };

        if (node.then)
          walk(node.then, predicates.concat(group), `${path}.then`);
        if (node.otherwise) {
          walk(
            node.otherwise,
            predicates.concat({
              ...group,
              negate: true,
              source: `${node.id || path}:otherwise`,
            }),
            `${path}.otherwise`,
          );
        }
        return;
      }

      if (
        type === "APPLY_DISCOUNT" &&
        normalizeKey(node.value?.type).toUpperCase() === "VOLUME"
      ) {
        addCandidate(node, predicates, path);
        return;
      }

      if (node.entryPoint)
        walk(node.entryPoint, predicates, `${path}.entryPoint`);

      Object.keys(node).forEach((key) => {
        if (
          [
            "entryPoint",
            "conditions",
            "then",
            "otherwise",
            "value",
            "breaks",
            "discount",
            "eligibility",
            "compactFormat",
            "dimensions",
            "position",
            "tooltips",
          ].includes(key)
        ) {
          return;
        }
        const value = node[key];
        if (!value || typeof value !== "object") return;
        if (Array.isArray(value))
          value.forEach((child, i) =>
            walk(child, predicates, `${path}.${key}[${i}]`),
          );
        else walk(value, predicates, `${path}.${key}`);
      });
    }

    walk(cfg.customLogic || cfg, rootPredicates, `config:${entry.id}`);
    return candidates;
  }

  function volumeNodeToTiers(volumeNode, currencySymbol) {
    const breaks = volumeNode?.value?.breaks;
    if (!Array.isArray(breaks) || !breaks.length) return [];

    return breaks
      .map((b) => {
        const qty = toInt(b.minimumQuantity || b.qty || b.quantity);
        const d = b.discount || b.value || {};
        if (!qty || qty < 1) return null;

        const type = normalizeKey(d.type).toUpperCase();

        if (type === "PERCENTAGE") {
          const pct = toNumber(d.percentage || d.value || d.amount);
          if (pct == null) return null;
          return {
            qty,
            label: `${formatNumber(pct)}%`,
            badgeText: `-${formatNumber(pct)}%`,
            discountType: type,
          };
        }

        if (type === "FIXED_AMOUNT" || type === "AMOUNT_OFF") {
          const amt = toNumber(d.amount || d.value);
          if (amt == null) return null;
          return {
            qty,
            label: `${currencySymbol} ${formatNumber(amt)}`,
            badgeText: `-${currencySymbol} ${formatNumber(amt)}`,
            discountType: type,
          };
        }

        if (type === "FIXED_PRICE" || type === "PRICE") {
          const amt = toNumber(d.amount || d.price || d.value);
          if (amt == null) return null;
          return {
            qty,
            label: `${currencySymbol} ${formatNumber(amt)}`,
            badgeText: `${currencySymbol} ${formatNumber(amt)} cad.`,
            discountType: type,
          };
        }

        if (type === "FREE" || type === "FREE_GIFT") {
          return {
            qty,
            label: "gratis",
            badgeText: "gratis",
            discountType: type,
          };
        }

        return null;
      })
      .filter(Boolean)
      .sort((a, b) => a.qty - b.qty);
  }

  function applyIncludeExclude(match, includeOrExclude) {
    if (match == null) return null;
    const mode = normalizeKey(includeOrExclude || "INCLUDE").toUpperCase();
    return mode === "EXCLUDE" ? !match : match;
  }

  function evalResult(match, score, reason) {
    return { match, score: score || 0, reason: reason || "" };
  }

  function evalProductEligibility(condition, ctx) {
    const elig =
      condition.eligibility || condition.productEligibility || condition || {};
    const type = normalizeKey(
      elig.type ||
        condition.eligibilityType ||
        condition.productEligibilityType,
    ).toUpperCase();

    let base = null;
    let score = 0;

    if (!type || type === "ALL_PRODUCTS") {
      base = true;
      score = 1;
    } else if (type === "SPECIFIC_PRODUCTS" || type === "PRODUCTS") {
      const ids = unique([
        ...extractListFromKeys(elig, [
          "asListOfIds",
          "selectedProducts",
          "selectedProductIds",
          "productIds",
          "ids",
        ]),
        ...compactKeys(elig.compactFormat),
      ]);
      base =
        ids.length && ctx.productId
          ? ids.includes(String(ctx.productId))
          : null;
      score = base ? 80 : 0;
    } else if (
      type === "SPECIFIC_PRODUCT_VARIANTS" ||
      type === "PRODUCT_VARIANTS" ||
      type === "SPECIFIC_VARIANTS" ||
      type === "VARIANTS"
    ) {
      const variantIds = unique([
        ...extractListFromKeys(elig, [
          "asListOfIds",
          "selectedProductVariants",
          "selectedVariantIds",
          "variantIds",
          "ids",
        ]),
        ...compactValues(elig.compactFormat),
      ]);
      const productIds = compactKeys(elig.compactFormat);

      if (ctx.variantId && variantIds.length) {
        base = variantIds.includes(String(ctx.variantId));
        score = base ? 100 : 0;
      } else if (ctx.productId && productIds.length) {
        base = productIds.includes(String(ctx.productId));
        score = base ? 70 : 0;
      } else {
        base = null;
      }
    } else if (type === "SPECIFIC_COLLECTIONS" || type === "COLLECTIONS") {
      const ids = unique([
        ...extractListFromKeys(elig, [
          "asListOfIds",
          "selectedCollections",
          "selectedCollectionIds",
          "collectionIds",
          "ids",
        ]),
        ...compactKeys(elig.compactFormat),
        ...compactValues(elig.compactFormat),
      ]);
      base =
        ids.length && ctx.collectionIds.length
          ? ids.some((id) => ctx.collectionIds.includes(String(id)))
          : null;
      score = base ? 60 : 0;
    } else if (
      type === "SPECIFIC_VENDORS" ||
      type === "VENDORS" ||
      type === "PRODUCT_VENDORS"
    ) {
      const vendors = unique(
        extractListFromKeys(elig, [
          "vendors",
          "selectedVendors",
          "selectedVendorNames",
          "names",
          "values",
        ]).map(normalizeKey),
      );
      base =
        vendors.length && ctx.vendor
          ? vendors.includes(normalizeKey(ctx.vendor))
          : null;
      score = base ? 50 : 0;
    } else if (
      type === "SPECIFIC_PRODUCT_TAGS" ||
      type === "PRODUCT_TAGS" ||
      type === "PRODUCTS_WITH_SPECIFIC_TAGS" ||
      type === "TAGS"
    ) {
      const tags = unique(
        extractListFromKeys(elig, [
          "tags",
          "selectedTags",
          "selectedProductTags",
          "productTags",
          "values",
        ]).map(normalizeKey),
      );
      const productTags = ctx.tags.map(normalizeKey);
      base =
        tags.length && productTags.length
          ? tags.some((tag) => productTags.includes(tag))
          : null;
      score = base ? 50 : 0;
    } else if (
      type === "PRODUCT_VARIANTS_ALREADY_ON_SALE" ||
      type === "VARIANTS_ALREADY_ON_SALE" ||
      type === "PRODUCTS_ALREADY_ON_SALE" ||
      type.includes("ON_SALE") ||
      type.includes("ALREADY_ON_SALE")
    ) {
      base = ctx.isOnSale;
      score = base ? 40 : 0;
    } else if (type === "PRODUCT_TYPE" || type === "SPECIFIC_PRODUCT_TYPES") {
      const productTypes = unique(
        extractListFromKeys(elig, [
          "productTypes",
          "selectedProductTypes",
          "types",
          "values",
        ]).map(normalizeKey),
      );
      base =
        productTypes.length && ctx.productType
          ? productTypes.includes(normalizeKey(ctx.productType))
          : null;
      score = base ? 30 : 0;
    } else if (type.includes("METAFIELD")) {
      base = matchMetafieldEligibility(
        elig,
        type.includes("VARIANT")
          ? ctx.variantMetafields
          : ctx.productMetafields,
      );
      score = base ? 45 : 0;
    } else {
      base = null;
      warn(
        "Unknown product eligibility type, letting DOPP API/fallback decide",
        type,
        elig,
      );
    }

    return evalResult(
      applyIncludeExclude(base, condition.includeOrExclude),
      score,
      `PRODUCT_ELIGIBILITY:${type || "UNKNOWN"}`,
    );
  }

  function matchMetafieldEligibility(elig, ctxMetafields) {
    if (!Array.isArray(ctxMetafields) || !ctxMetafields.length) return null;

    const namespace = normalizeStr(
      elig.namespace || elig.metafieldNamespace || elig.metafield?.namespace,
    );
    const key = normalizeStr(
      elig.key || elig.metafieldKey || elig.metafield?.key,
    );
    const expectedValues = parseList(
      elig.value || elig.values || elig.metafieldValue || elig.metafield?.value,
    ).map(normalizeKey);

    if (!namespace && !key && !expectedValues.length) return null;

    return ctxMetafields.some((mf) => {
      if (!mf || typeof mf !== "object") return false;
      const nsOk = namespace
        ? normalizeKey(mf.namespace) === normalizeKey(namespace)
        : true;
      const keyOk = key ? normalizeKey(mf.key) === normalizeKey(key) : true;
      const valueOk = expectedValues.length
        ? expectedValues.includes(normalizeKey(mf.value))
        : true;
      return nsOk && keyOk && valueOk;
    });
  }

  function evalCustomerEligibility(condition, ctx) {
    const elig =
      condition.eligibility || condition.customerEligibility || condition || {};
    const type = normalizeKey(
      elig.type || condition.customerEligibilityType,
    ).toUpperCase();
    const customer = ctx.customer || {};
    const customerTags = parseList(
      customer.tags || ctx.customer?.tags || [],
    ).map(normalizeKey);
    const customerSegments = parseIdList(
      customer.segments || ctx.customer?.segments || [],
    );

    let base = null;
    let score = 0;

    if (!type || type === "ALL_CUSTOMERS") {
      base = true;
      score = 1;
    } else if (type.includes("TAG")) {
      const tags = extractListFromKeys(elig, [
        "tags",
        "selectedTags",
        "customerTags",
        "values",
      ]).map(normalizeKey);
      base =
        tags.length && customerTags.length
          ? tags.some((tag) => customerTags.includes(tag))
          : null;
      score = base ? 20 : 0;
    } else if (type.includes("SEGMENT")) {
      const ids = extractListFromKeys(elig, [
        "ids",
        "segmentIds",
        "selectedSegments",
        "selectedSegmentIds",
        "values",
      ]);
      base =
        ids.length && customerSegments.length
          ? ids.some((id) => customerSegments.includes(String(id)))
          : null;
      score = base ? 20 : 0;
    } else if (type.includes("CUSTOMER")) {
      const ids = extractListFromKeys(elig, [
        "ids",
        "customerIds",
        "selectedCustomers",
        "selectedCustomerIds",
        "values",
      ]);
      base =
        ids.length && customer.id ? ids.includes(String(customer.id)) : null;
      score = base ? 20 : 0;
    } else {
      base = null;
    }

    return evalResult(
      applyIncludeExclude(base, condition.includeOrExclude),
      score,
      `CUSTOMER_ELIGIBILITY:${type || "UNKNOWN"}`,
    );
  }

  function evalMarketEligibility(condition, ctx) {
    const elig =
      condition.eligibility || condition.marketEligibility || condition || {};
    const ids = unique([
      ...extractListFromKeys(elig, [
        "ids",
        "marketIds",
        "selectedMarkets",
        "selectedMarketIds",
        "values",
      ]),
      ...compactKeys(elig.compactFormat),
      ...compactValues(elig.compactFormat),
    ]);

    let base = null;
    if (ids.length && ctx.marketId) {
      base = ids.includes(String(ctx.marketId));
    }

    return evalResult(
      applyIncludeExclude(base, condition.includeOrExclude),
      base ? 20 : 0,
      "MARKET_ELIGIBILITY",
    );
  }

  function evalSellingPlan(condition, ctx) {
    const appliesTo = normalizeKey(
      condition.appliesTo || condition.sellingPlanAppliesTo || condition.value,
    ).toUpperCase();
    const hasSellingPlan =
      ctx.hasSellingPlan != null
        ? ctx.hasSellingPlan
        : ctx.sellingPlanId
          ? true
          : null;

    let base = null;

    if (
      !appliesTo ||
      appliesTo === "ALL_SELLING_PLANS" ||
      appliesTo === "BOTH"
    ) {
      base = true;
    } else if (
      appliesTo === "ONE_TIME_ONLY" ||
      appliesTo === "ONE_TIME" ||
      appliesTo === "ONE_TIME_PURCHASES"
    ) {
      base = hasSellingPlan == null ? null : !hasSellingPlan;
    } else if (
      appliesTo === "SUBSCRIPTIONS_ONLY" ||
      appliesTo === "SUBSCRIPTION_ONLY" ||
      appliesTo === "SELLING_PLANS_ONLY"
    ) {
      base = hasSellingPlan == null ? null : hasSellingPlan;
    }

    return evalResult(
      applyIncludeExclude(base, condition.includeOrExclude),
      base ? 10 : 0,
      `SELLING_PLAN:${appliesTo || "UNKNOWN"}`,
    );
  }

  function evalCondition(condition, ctx) {
    if (!condition || typeof condition !== "object")
      return evalResult(null, 0, "empty");

    const type = normalizeKey(condition.type).toUpperCase();

    if (
      type === "PRODUCT_ELIGIBILITY" ||
      type.includes("PRODUCT_ELIGIBILITY")
    ) {
      return evalProductEligibility(condition, ctx);
    }

    if (
      type === "CUSTOMER_ELIGIBILITY" ||
      type.includes("CUSTOMER_ELIGIBILITY")
    ) {
      return evalCustomerEligibility(condition, ctx);
    }

    if (type === "MARKET_ELIGIBILITY" || type.includes("MARKET")) {
      return evalMarketEligibility(condition, ctx);
    }

    if (
      type === "SELLING_PLAN" ||
      type.includes("SELLING_PLAN") ||
      type.includes("SUBSCRIPTION")
    ) {
      return evalSellingPlan(condition, ctx);
    }

    warn(
      "Unknown/non-local condition, letting DOPP API/fallback decide",
      type,
      condition,
    );
    return evalResult(null, 0, type || "UNKNOWN");
  }

  function evalPredicateGroup(group, ctx) {
    const conditions = Array.isArray(group.conditions) ? group.conditions : [];
    const matchType = normalizeKey(group.matchType || "AND").toUpperCase();
    const results = conditions.map((condition) =>
      evalCondition(condition, ctx),
    );
    const score = results.reduce((sum, r) => sum + (r.score || 0), 0);

    let match;
    if (!results.length) {
      match = true;
    } else if (matchType === "OR" || matchType === "ANY") {
      if (results.some((r) => r.match === true)) match = true;
      else if (results.every((r) => r.match === false)) match = false;
      else match = null;
    } else {
      if (results.some((r) => r.match === false)) match = false;
      else if (results.every((r) => r.match === true)) match = true;
      else match = null;
    }

    if (group.negate && match != null) match = !match;

    return { match, score, details: results };
  }

  function evaluateCandidateLocally(candidate, ctx) {
    const groups = candidate.predicates || [];
    let score = 0;
    let hasUnknown = false;

    for (const group of groups) {
      const result = evalPredicateGroup(group, ctx);
      score += result.score || 0;

      if (result.match === false) {
        return { match: false, score, hasUnknown, details: result.details };
      }

      if (result.match == null) {
        hasUnknown = true;
      }
    }

    if (hasUnknown && STRICT_UNKNOWN_CONDITIONS) {
      return { match: false, score, hasUnknown };
    }

    return { match: true, score, hasUnknown };
  }

  function getDoppApi() {
    return (
      window.RegiosDOPP && window.RegiosDOPP.api && window.RegiosDOPP.api.v0
    );
  }

  function apiArgsForCtx(ctx, quantity) {
    const args = { quantity };

    const variantIdNum = toNumber(ctx.variantId);
    if (variantIdNum != null) args.variantId = variantIdNum;

    if (ctx.customer !== undefined) args.customer = ctx.customer;
    if (ctx.collectionIds.length)
      args.collectionIds = ctx.collectionIds
        .map((id) => toNumber(id))
        .filter((id) => id != null);
    if (ctx.lineItemProperties)
      args.lineItemProperties = ctx.lineItemProperties;

    return args;
  }

  function globalApiArgsForCtx(ctx, quantity) {
    const productId = toNumber(ctx.productId);
    const variantId = toNumber(ctx.variantId);
    const regularPriceInCents = toNumber(ctx.priceInCents);

    if (productId == null || variantId == null || regularPriceInCents == null)
      return null;

    const args = {
      productId,
      variantId,
      regularPriceInCents,
      quantity,
    };

    if (ctx.collectionIds.length)
      args.collectionIds = ctx.collectionIds
        .map((id) => toNumber(id))
        .filter((id) => id != null);
    if (ctx.compareAtPriceInCents != null)
      args.compareAtPriceInCents = ctx.compareAtPriceInCents;
    if (ctx.handle) args.handle = ctx.handle;
    if (ctx.vendor) args.vendor = ctx.vendor;
    if (ctx.url) args.url = ctx.url;
    if (ctx.tags.length) args.tags = ctx.tags;
    if (Array.isArray(ctx.productMetafields) && ctx.productMetafields.length)
      args.metafields = ctx.productMetafields;
    if (ctx.customer !== undefined) args.customer = ctx.customer;
    if (ctx.lineItemProperties) args.properties = ctx.lineItemProperties;

    return args;
  }

  function apiResultHasDiscount(result) {
    const regular = toNumber(result?.regularPrice?.amount);
    const sale = toNumber(result?.salePrice?.amount);
    if (regular == null || sale == null) return null;
    return sale < regular;
  }

  async function validateCandidateWithDoppApi(candidate, ctx) {
    const api = getDoppApi();
    if (!api) return null;

    const quantitiesToTry = unique([
      String(candidate.tiers[0]?.qty || 1),
      String(candidate.tiers[candidate.tiers.length - 1]?.qty || 1),
    ])
      .map((x) => toInt(x))
      .filter((x) => x && x > 0);

    for (const qty of quantitiesToTry) {
      try {
        let result = null;

        if (
          api.productPage &&
          typeof api.productPage.calculateDiscountedPrices === "function"
        ) {
          result = await api.productPage.calculateDiscountedPrices(
            apiArgsForCtx(ctx, qty),
          );
        } else if (typeof api.calculateDiscountedPrices === "function") {
          const args = globalApiArgsForCtx(ctx, qty);
          if (!args) return null;
          result = await api.calculateDiscountedPrices(args);
        }

        const hasDiscount = apiResultHasDiscount(result);
        if (hasDiscount === true) return true;
      } catch (e) {
        warn("DOPP API validation failed", e);
        return null;
      }
    }

    return false;
  }

  async function pickBestCandidate(configs, ctx) {
    const candidates = configs.flatMap((entry) =>
      collectVolumeCandidates(entry),
    );

    const locallyAllowed = candidates
      .map((candidate, index) => ({
        candidate,
        index,
        local: evaluateCandidateLocally(candidate, ctx),
      }))
      .filter((item) => item.local.match)
      .sort((a, b) => {
        const scoreDiff = (b.local.score || 0) - (a.local.score || 0);
        return scoreDiff || a.index - b.index;
      });

    if (!locallyAllowed.length) return null;

    const api = getDoppApi();
    if (api) {
      for (const item of locallyAllowed) {
        const apiMatch = await validateCandidateWithDoppApi(
          item.candidate,
          ctx,
        );
        if (apiMatch === true) return item.candidate;
        if (apiMatch === null && !item.local.hasUnknown) return item.candidate;
      }

      return null;
    }

    return locallyAllowed[0].candidate;
  }

  function buildTiles(container, tiers) {
    const grid = container.querySelector("[data-jkl-bmsm-grid]");
    if (!grid) return;

    const sig = tiers
      .map((t) => `${t.qty}:${t.badgeText || t.label}`)
      .join("|");
    if (container.dataset.jklBmsmSig === sig && grid.children.length) return;
    container.dataset.jklBmsmSig = sig;

    grid.innerHTML = tiers
      .map((t) => {
        const qtyText = t.qty === 1 ? "1 pack" : `${t.qty} pack`;
        return (
          `<button type="button" class="jkl-bmsm__tile" data-jkl-bmsm-tier data-qty="${escapeHtml(t.qty)}" aria-pressed="false">` +
          `<span class="jkl-bmsm__qty">${escapeHtml(qtyText)}</span>` +
          `<span class="jkl-bmsm__badge">${escapeHtml(t.badgeText || `-${t.label}`)}</span>` +
          `</button>`
        );
      })
      .join("");

    container.style.display = "";
  }

  function hide(container) {
    container.style.display = "none";
    container.dataset.jklBmsmSig = "";
  }

  function setSelected(container, btnOrNull) {
    container.querySelectorAll("[data-jkl-bmsm-tier]").forEach((x) => {
      const isSel = !!btnOrNull && x === btnOrNull;
      x.classList.toggle("is-selected", isSel);
      x.setAttribute("aria-pressed", isSel ? "true" : "false");
    });
  }

  function syncToQty(container, qty) {
    const btn = Array.from(
      container.querySelectorAll("[data-jkl-bmsm-tier]"),
    ).find((b) => toInt(b.getAttribute("data-qty")) === qty);

    if (btn) setSelected(container, btn);
    else setSelected(container, null);
  }

  async function renderAll() {
    const seq = ++renderSeq;
    const configs = readRegiosConfigs();
    const containers = Array.from(document.querySelectorAll("[data-jkl-bmsm]"));

    if (!containers.length) return;

    if (!configs.length) {
      log("Regios configs not ready yet.");
      if (retryCount >= MAX_CONFIG_RETRIES) containers.forEach(hide);
      else scheduleRetry();
      return;
    }

    for (const container of containers) {
      if (seq !== renderSeq) return;

      const ctx = getCtx(container);
      if (!ctx.productId) {
        hide(container);
        continue;
      }

      const candidate = await pickBestCandidate(configs, ctx);
      if (seq !== renderSeq) return;

      if (!candidate || !candidate.tiers.length) {
        hide(container);
        log("No BMSM tiers for container", ctx);
        continue;
      }

      buildTiles(container, candidate.tiers);
      syncToQty(container, getCurrentQty(container));
      log("Rendered BMSM tiers", { ctx, candidate });
    }
  }

  function scheduleRender() {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      renderAll();
    });
  }

  function scheduleRetry() {
    if (retryTimer || retryCount >= MAX_CONFIG_RETRIES) return;

    retryCount += 1;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      scheduleRender();
    }, CONFIG_RETRY_DELAY_MS);
  }

  function dispatchRegiosVariantChange(container, qty) {
    const variantId = toNumber(readSelectedVariantId(container));
    if (variantId == null) return;

    const detail = {
      selectedVariant: {
        id: variantId,
      },
      quantity: qty,
    };

    const price = toNumber(
      container.getAttribute("data-price-in-cents") ||
        container.getAttribute("data-regular-price-in-cents"),
    );
    if (price != null) detail.selectedVariant.price = price;

    window.dispatchEvent(
      new CustomEvent("regios-dopp:variant-change", { detail }),
    );
  }

  document.addEventListener("click", function (event) {
    const btn = event.target.closest("[data-jkl-bmsm-tier]");
    if (!btn) return;

    const container = btn.closest("[data-jkl-bmsm]");
    if (!container) return;

    const qty = toInt(btn.getAttribute("data-qty"));
    if (!qty || qty < 1) return;

    const qtyInput = getQtyInput(container);
    if (qtyInput) {
      qtyInput.value = qty;
      qtyInput.dispatchEvent(new Event("input", { bubbles: true }));
      qtyInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    setSelected(container, btn);
    dispatchRegiosVariantChange(container, qty);
  });

  document.addEventListener("input", function (event) {
    const input = event.target;
    if (!input || input.name !== "quantity") return;

    const qty = toInt(input.value);
    const formId = input.getAttribute("form") || input.closest("form")?.id;

    const selector = formId
      ? `[data-jkl-bmsm][data-form-id="${cssEscape(formId)}"]`
      : "[data-jkl-bmsm]";

    document.querySelectorAll(selector).forEach((container) => {
      if (qty) syncToQty(container, qty);
      else setSelected(container, null);
      if (qty) dispatchRegiosVariantChange(container, qty);
    });
  });

  document.addEventListener("change", function (event) {
    const input = event.target;
    if (!input || input.name !== "id") return;
    scheduleRender();
  });

  [
    "variant:change",
    "product:variant-change",
    "product:change",
    "theme:variant:change",
    "regios-dopp:api-initialized",
  ].forEach((eventName) => {
    window.addEventListener(eventName, scheduleRender);
  });

  window.addEventListener("regios-dopp:variant-change", function () {
    setTimeout(scheduleRender, 0);
  });

  const obs = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const n of m.addedNodes || []) {
        if (!(n instanceof Element)) continue;
        if (
          n.matches?.("[data-jkl-bmsm]") ||
          n.querySelector?.("[data-jkl-bmsm]")
        ) {
          scheduleRender();
          return;
        }
      }
    }
  });

  function startObserver() {
    const root = document.body;
    if (!root) {
      document.addEventListener("DOMContentLoaded", startObserver, {
        once: true,
      });
      return;
    }
    obs.observe(root, { childList: true, subtree: true });
  }

  function onReady() {
    scheduleRender();
    startObserver();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", onReady, { once: true });
  } else {
    onReady();
  }
})();
